import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {Skeleton} from '@ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {Trans} from '@ui/i18n/trans';
import {FormattedPrice} from '@common/billing/formatted-price';
import {Button} from '@ui/buttons/button';
import {Link} from 'react-router';
import {setInLocalStorage} from '@ui/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';
import {UpsellLabel} from '@common/billing/pricing-table/upsell-label';

const CYCLE_TRANS_KEYS: Record<UpsellBillingCycle, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Semiannual',
  yearly: 'Annual',
};

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
}: PricingTableProps) {
  const query = useProducts(productLoader);
  return (
    <div
      className={clsx(
        'grid w-full grid-cols-1 items-stretch gap-16 pb-12 md:flex md:justify-center md:gap-24 md:overflow-x-auto md:overflow-y-visible md:pb-20',
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key="plan-list"
            plans={query.data.products}
            selectedPeriod={selectedCycle}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
}
function PlanList({plans, selectedPeriod}: PlanListProps) {
  const {isLoggedIn, isSubscribed} = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);
  return (
    <Fragment>
      {filteredPlans.map(plan => {
        const price = findBestPrice(selectedPeriod, plan.prices);

        let upgradeRoute;
        if (!isLoggedIn) {
          upgradeRoute = `/register?redirectFrom=pricing`;
        }
        if (isSubscribed) {
          upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
        }
        if (isLoggedIn && !plan.free) {
          upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
        }

        return (
          <m.div
            key={plan.id}
            {...opacityAnimation}
            className={clsx(
              'relative flex w-full flex-col rounded-2xl border bg p-20 shadow-sm sm:p-24 md:block md:min-w-240 md:max-w-350 md:rounded-panel md:px-28 md:py-28 md:shadow-lg',
              plan.recommended
                ? 'border-primary/60 shadow-lg ring-1 ring-primary/30 md:border-divider md:ring-0'
                : 'border-divider',
              plan.free && 'order-last md:order-none',
            )}
          >
            <Chip
              radius="rounded"
              size="sm"
              className={clsx(
                'mb-12 w-min md:mb-20',
                !plan.recommended && 'hidden md:inline-flex md:invisible',
              )}
            >
              <Trans message="Most popular" />
            </Chip>
            <div className="mb-24 md:mb-32">
              <div className="mb-4 text-xl font-semibold">
                <Trans message={plan.name} />
              </div>
              {!plan.free && (
                <div className="mb-12 text-sm font-medium text-primary">
                  <Trans message={CYCLE_TRANS_KEYS[selectedPeriod]} />
                </div>
              )}
              <div className="text-sm leading-6 text-muted md:leading-normal">
                <Trans message={plan.description} />
              </div>
            </div>
            <div>
              {price ? (
                <FormattedPrice
                  priceClassName="font-bold text-3xl sm:text-4xl"
                  periodClassName="text-muted text-xs"
                  variant="separateLine"
                  price={price}
                />
              ) : (
                <div className="text-3xl font-bold sm:text-4xl">
                  <Trans message="Free" />
                </div>
              )}
              {price && <UpsellLabel products={plans} cycle={selectedPeriod} />}
              <div className="mt-24 md:mt-60">
                <Button
                  variant={plan.recommended ? 'flat' : 'outline'}
                  color="primary"
                  className="w-full"
                  size="md"
                  elementType={upgradeRoute ? Link : undefined}
                  disabled={!upgradeRoute}
                  onClick={() => {
                    if (isLoggedIn || !plan) return;
                    setInLocalStorage('be.onboarding.selected', {
                      productId: plan.id,
                      priceId: price?.id ?? null,
                    });
                  }}
                  to={upgradeRoute}
                >
                  {plan.free ? (
                    <Trans message="Get started" />
                  ) : (
                    <Trans message="Upgrade plan" />
                  )}
                </Button>
              </div>
              <ProductFeatureList product={plan} />
            </div>
          </m.div>
        );
      })}
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-2xl border p-20 shadow-sm sm:p-24 md:max-w-350 md:rounded-lg md:px-28 md:py-90 md:shadow-lg"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-24 md:mb-40" />
      <Skeleton className="mb-24 h-30 md:mb-40" />
      <Skeleton className="mb-24 h-40 md:mb-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
